import { tz } from '@date-fns/tz'
import { format, parseISO } from 'date-fns'
import { fr } from 'date-fns/locale'
import { DEFAULT_LOCALE, DEFAULT_TIMEZONE } from '@/constants/geo'

/**
 * The one zone-aware date-time pair: format an instant in a place's zone, and
 * convert both ways between an instant and what an
 * `<input type="datetime-local">` shows (epic #1857 rule 5 — "convert at the
 * edges only, with an explicit `timeZone`").
 *
 * Everything here takes an **instant** and an **IANA zone name**, and neither
 * is optional. There is deliberately no default zone: a default is how the
 * browser's zone, or Paris, silently becomes the answer for a site that is in
 * neither. The zone to pass is the one the API resolved for the work's site —
 * the site and intervention responses carry it (#1881) — and the resolution
 * order lives server-side in `resolveTimezone`, not here.
 *
 * `france-time.utils.ts` holds the `Europe/Paris` wrappers over these, so that
 * the transition (#1866) can delete the wrappers without touching the rule.
 *
 * @see docs/standards/datetime-strategy.md
 */

/** What every date-time surface in the product prints, in French. */
export const DATETIME_PATTERN = "dd/MM/yyyy 'à' HH:mm"

/** The same surfaces without the time — « 20/09/2026 ». */
export const DAY_PATTERN = 'dd/MM/yyyy'

/** A calendar day as a machine writes it, e.g. a `DatePicker`'s value. */
export const ISO_DAY_PATTERN = 'yyyy-MM-dd'

/** What `<input type="datetime-local">` reads and writes. Not a display format. */
const DATETIME_LOCAL_PATTERN = "yyyy-MM-dd'T'HH:mm"

/** `YYYY-MM-DDTHH:mm` — what {@link DATETIME_LOCAL_PATTERN} renders to. */
const DATETIME_LOCAL_MINUTE_LENGTH = 16

/**
 * The shape of an IANA zone name: `Area/Location`, up to two sub-locations
 * (`America/Argentina/Buenos_Aires`), or the bare `UTC`.
 *
 * Deliberately the same expression as `isValidIanaTimezone`
 * (`apps/api/src/shared/utils/timezone.utils.ts`) — its doc carries the full
 * reasoning, including why `Intl.supportedValuesOf` is not the allow-list. It
 * is duplicated rather than shared because the two ends of this epic are being
 * migrated by different people and the api file is the other lane's; the fence
 * belongs in `@depanneo/utils` once both sides have landed.
 */
const IANA_NAME_PATTERN =
  /^(?:UTC|[A-Z][A-Za-z0-9_+-]*(?:\/[A-Z][A-Za-z0-9_+-]*){1,2})$/

/**
 * Whether this runtime can format in `timeZone`, and whether `timeZone` is a
 * zone at all rather than an offset.
 *
 * This fence is the whole reason "no default zone" means anything. Without it
 * the promise is only a TYPE: `Intl` treats an absent or empty `timeZone` as
 * the SYSTEM zone instead of erroring, so the natural `site.timezone ?? ''` —
 * `sites.timezone` is nullable, NULL meaning « inherit » — would sail through
 * and hand back the browser's own zone, differently in each direction.
 * Measured before this guard existed: a `''` zone formatted one instant as UTC
 * and read a typed wall-clock back as the browser's, so a form that never
 * touched the field still moved the appointment on save.
 *
 * An offset literal is refused for the same reason the standard bans it from
 * the column: `+02:00` is Paris for half the year and wrong for the other half,
 * and only a named zone carries its own DST history.
 */
function isUsableZone(timeZone: string): boolean {
  if (typeof timeZone !== 'string' || !IANA_NAME_PATTERN.test(timeZone)) {
    return false
  }

  try {
    new Intl.DateTimeFormat('fr-FR', { timeZone })
    return true
  } catch {
    return false
  }
}

/**
 * An instant, as it reaches the web: an ISO string with a real offset or `Z`,
 * or an already-parsed `Date`. A fake-`Z` wall-clock field is NOT one of these
 * — see the read trap in the standard.
 */
export type Instant = string | Date | null | undefined

function toDate(instant: Exclude<Instant, null | undefined>): Date {
  return typeof instant === 'string' ? parseISO(instant) : instant
}

/**
 * An instant → the wall-clock a reader in `timeZone` sees, in French, or `null`
 * when it cannot be formatted — an unreadable instant, or a zone that fails
 * {@link isUsableZone}.
 *
 * This is the core; {@link formatInZone} is it with a fallback. Callers that
 * have a better fallback of their own (the original string they were handed,
 * say) use this and keep it.
 *
 * It is `format(…, { in: tz(timeZone) })`, and no browser-local `Date` may sit
 * between the instant and the digits. Both `date-fns-tz` routes put one there:
 *
 * - `format(toZonedTime(…))` formats with the BROWSER still supplying the zone,
 *   so an offset or zone token (`XXX`, `O`) prints the browser's offset stapled
 *   to the site's digits — a value naming a moment hours from the one it shows.
 * - `formatInTimeZone` rebuilds the site's wall clock as a browser-local `Date`
 *   first, and a wall clock the BROWSER's zone skips does not exist there, so
 *   it rolls an hour forward (#2413). Measured: a Paris `03:30` on 28 March
 *   2027 printed `04:30` from Kyiv, and a Paris `02:30` on the US
 *   spring-forward Sunday printed `03:30` from New York — the same instant,
 *   different digits per viewer, a few hours a year per pair of zones.
 *
 * `TZDate` reads its fields from the zone's offset at the instant (`Intl`
 * under the hood) and never asks the browser, so every viewer gets the site's
 * digits and the offset tokens (`XXX`, `xxx`, `O`) name the site's offset. The
 * zone-NAME tokens are not `date-fns-tz`'s: `z`…`zzz` print `GMT+2` and `zzzz`
 * prints `GMT+02:00`, never « heure d’été d’Europe centrale ». No caller uses
 * them; a surface that names the zone (rule 7) uses {@link zoneLabelFrOrNull}.
 */
export function formatInZoneOrNull(
  instant: Instant,
  timeZone: string,
  pattern: string = DATETIME_PATTERN
): string | null {
  if (!instant || !isUsableZone(timeZone)) return null

  try {
    return format(toDate(instant), pattern, { locale: fr, in: tz(timeZone) })
  } catch {
    return null
  }
}

/**
 * An instant → the wall-clock a reader in `timeZone` sees, in French.
 *
 * @param instant - an ISO instant (offset or `Z`) or a `Date`
 * @param timeZone - the IANA zone resolved for the site, e.g. `Indian/Reunion`
 * @param pattern - date-fns pattern (default: `dd/MM/yyyy 'à' HH:mm`)
 * @returns the formatted string, `''` when there is nothing to show, or the
 *          input echoed back when it cannot be formatted — a formatter sitting
 *          in a table cell must not throw the render away, and an operator
 *          seeing the raw value knows something is wrong in a way a blank cell
 *          does not tell them
 */
export function formatInZone(
  instant: Instant,
  timeZone: string,
  pattern: string = DATETIME_PATTERN
): string {
  if (!instant) return ''

  return formatInZoneOrNull(instant, timeZone, pattern) ?? String(instant)
}

/**
 * An instant → the value an `<input type="datetime-local">` shows, in
 * `timeZone`.
 *
 * The input carries no zone of its own: it renders the wall-clock string it is
 * given and hands back whatever was typed into it. Left to the browser that
 * pair is the browser's zone, so an operator away from the site types one hour
 * and every read-only surface beside the form then prints another (#1532). Use
 * it with {@link zonedDatetimeLocalToISO} and the SAME zone — one direction on
 * its own reintroduces the skew, mirrored.
 *
 * The pair is NOT an involution on the one repeated hour a year, and cannot be:
 * two instants read as the same digits, so the way back has to pick one. On the
 * October Paris night, `02:30+02:00` shows as `02:30` and comes back as
 * `02:30+01:00` — an hour later. That is a property of the clock, not a bug to
 * fix here; what it means for a FORM is that re-saving a record whose date sits
 * in that hour moves it, even when the operator never touched the field. A form
 * that must not do that submits the date only when its value changed.
 *
 * @returns `YYYY-MM-DDTHH:mm`, or `''` when there is nothing selectable — an
 *          input's value has to be well-formed or empty, never a raw string,
 *          which is the one place this differs from {@link formatInZone}
 */
export function toZonedDatetimeLocalValue(
  instant: Instant,
  timeZone: string
): string {
  return formatInZoneOrNull(instant, timeZone, DATETIME_LOCAL_PATTERN) ?? ''
}

/** ICU can render midnight as hour 24 under `hour12: false`. */
const ICU_MIDNIGHT_HOUR = 24

/**
 * `YYYY-MM-DDTHH:mm`, optionally `:ss` — what every
 * `<input type="datetime-local">` in this app hands back. It carries no offset:
 * an input that stated one would not be a wall-clock, and reading it as one is
 * how a moment moves.
 *
 * The seconds are optional because a browser emits them only when a `step` asks
 * for them. A FRACTIONAL `step` would emit `:ss.sss`, which this refuses — no
 * input here sets one, and admitting a precision no caller can produce would be
 * guessing at a shape rather than describing one. If a fractional step ever
 * appears, widen this with it; the api's `CLIENT_DATE_SHAPE` shows the form.
 */
const DATETIME_LOCAL_SHAPE =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/

/**
 * How far ahead of UTC `timeZone` is at `instant`, in milliseconds.
 *
 * Deliberately the same implementation as `zoneOffsetMs`
 * (`apps/api/src/shared/utils/timezone.utils.ts`), for the same reason the IANA
 * fence above is: the two ends of this epic are being migrated by different
 * people, the api file is the other lane's, and the pair belongs in
 * `@depanneo/utils` once both sides have landed. Copying it is what lets the
 * two ends agree on an answer TODAY; a near-miss reimplementation would not.
 */
function zoneOffsetMs(timeZone: string, instant: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant)

  const part = (type: string): number =>
    Number(parts.find((p) => p.type === type)?.value)

  const hour = part('hour') === ICU_MIDNIGHT_HOUR ? 0 : part('hour')

  const localAsUtc = Date.UTC(
    part('year'),
    part('month') - 1,
    part('day'),
    hour,
    part('minute'),
    part('second')
  )

  if (Number.isNaN(localAsUtc)) {
    throw new Error(
      `Cannot resolve the ${timeZone} offset for ${instant.toISOString()}`
    )
  }

  // `formatToParts` has second granularity, so compare against whole seconds.
  return localAsUtc - (instant.getTime() - instant.getMilliseconds())
}

/**
 * The inverse: a wall-clock typed into `<input type="datetime-local">`, read as
 * `timeZone`'s local time → the instant the API stores.
 *
 * The offset is resolved here rather than by `date-fns-tz`'s `fromZonedTime`,
 * which resolved an AMBIGUOUS wall-clock by the viewer's own browser zone
 * (#1961): measured, `+02:00` from Honolulu and `+01:00` from Paris for the
 * identical call, so two operators typing the same `02:30` stored instants an
 * hour apart and neither saw anything wrong — both read back as `02:30` in the
 * site's zone. That is rule 5's "no ambient zone" broken inside the library, on
 * the one path whose whole job is to remove ambient zones.
 *
 * The offset is resolved TWICE on purpose: the first pass reads it at the
 * digits-as-UTC instant, which sits on the wrong side of a DST switch for a
 * wall clock in the small hours of a transition day.
 *
 * Neither DST edge has a correct answer, and the guarantee here is NOT which
 * side gets picked — it is that BOTH ends pick the same one. This is the api's
 * `wallClockToInstant`, algorithm for algorithm, because two implementations
 * that merely agree about Paris are how the two ends of one field come to
 * disagree about a site somewhere else.
 *
 * Which side that is depends on the SIGN of the zone's offset, so it is worth
 * not memorising the Paris case as a rule. The first pass reads the offset at
 * the digits-as-UTC instant, and which side of the transition that lands on
 * flips with the sign — measured: `Europe/Paris` resolves a skipped `02:30`
 * FORWARD past the gap (`03:30+02:00`) and a repeated `02:30` to its SECOND,
 * winter occurrence (`02:30+01:00`), while `America/Miquelon` — a French
 * collectivity, so a `sites.timezone` this app can really be handed — resolves
 * its skipped `02:30` BACKWARD (`01:30-03:00`) and its repeated `01:30` to its
 * FIRST, summer one (`01:30-02:00`).
 *
 * @param value - `YYYY-MM-DDTHH:mm`, as the input hands it over
 * @param timeZone - the IANA zone the typed wall-clock belongs to
 * @returns ISO 8601 with `Z`, or `null` when there is nothing to store — the
 *          caller decides whether that is an error, since this helper has no
 *          idea whether the field was required
 */
export function zonedDatetimeLocalToISO(
  value: string,
  timeZone: string
): string | null {
  if (!value || !isUsableZone(timeZone)) return null

  const match = DATETIME_LOCAL_SHAPE.exec(value)
  if (!match) return null

  const [, year, month, day, hour, minute, second] = match

  const digitsAsUtc = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second ?? 0)
  )

  // `Date.UTC` range-rolls rather than rejecting: 30 February becomes 2 March
  // and `09:60` becomes 10:00. Read the fields back and require the ones asked
  // for, so a value nobody typed is refused instead of silently persisted as a
  // different moment — `fromZonedTime` used to swallow these too.
  const roundTrip = new Date(digitsAsUtc)
  const asked =
    roundTrip.getUTCFullYear() === Number(year) &&
    roundTrip.getUTCMonth() === Number(month) - 1 &&
    roundTrip.getUTCDate() === Number(day) &&
    roundTrip.getUTCHours() === Number(hour) &&
    roundTrip.getUTCMinutes() === Number(minute) &&
    roundTrip.getUTCSeconds() === Number(second ?? 0)

  if (!asked) return null

  try {
    const provisional = digitsAsUtc - zoneOffsetMs(timeZone, roundTrip)
    const instant = new Date(
      digitsAsUtc - zoneOffsetMs(timeZone, new Date(provisional))
    )

    return Number.isNaN(instant.getTime()) ? null : instant.toISOString()
  } catch {
    return null
  }
}

/**
 * The same conversion for a `Date`-valued picker — `DateTimePicker`, which
 * builds its value with `new Date(year, month, day, hours, minutes)` and so
 * carries the operator's digits in a `Date`'s LOCAL fields rather than naming
 * an instant.
 *
 * That shape is a wall clock wearing a `Date`'s clothes: its `getTime()` is the
 * BROWSER's reading of those digits and means nothing here. Both helpers below
 * go through the `datetime-local` pair above rather than re-deriving anything,
 * so the two widgets cannot disagree about the SITE's zone.
 *
 * The `Date` shape is not preferred, it is inherited: `<input
 * type="datetime-local">` is the better widget, is string-valued end to end and
 * needs neither of these. Use them only where the picker in hand is
 * `Date`-valued, always as a PAIR, and always with the same `timeZone` — one
 * direction alone reintroduces the skew, mirrored (#2087).
 */

/**
 * A wall clock already resolved in the right zone — whatever
 * {@link toZonedDatetimeLocalValue} produced — → the `Date` a `Date`-valued
 * picker must be given to show it.
 *
 * Exported because a component that already holds the `datetime-local` value
 * (as the prefill KEY of an effect, where an object prop would refire it
 * forever) must not have to re-derive the `Date` by a second route.
 *
 * **A `Date` cannot hold every wall clock**, and that is what this shape gives
 * up against the string one. `parseISO` on an offset-less value builds the
 * `Date` out of the BROWSER's local fields, and an hour the browser's own zone
 * SKIPS does not exist there: measured under `TZ=Europe/Paris`,
 * `parseISO('2026-03-29T02:30')` reads back as 03:30. A site in another zone has
 * a perfectly ordinary 02:30 that night, so the picker would show — and, since
 * the writer reads those same rolled fields, store — an hour nobody chose.
 *
 * So the digits are read back and required. A value that did not survive is
 * refused rather than rolled: the operator gets an empty picker they have to
 * fill, which is visible, instead of a prefilled one that is an hour out, which
 * is not. It costs a prefill for one hour a year, in the BROWSER's zone, on a
 * widget that is being retired.
 *
 * The repeated hour needs no such guard: two instants read as the same digits,
 * and the digits are all that is taken from here — which of them is meant is
 * decided by {@link zonedDatetimeLocalToISO} in the SITE's zone, where it
 * belongs.
 *
 * @returns `undefined` for an empty, unreadable or unrepresentable value — what
 *          such a picker takes for « nothing selected »
 */
export function datetimeLocalToPickerDate(
  value: string | null | undefined
): Date | undefined {
  if (!value) return undefined

  const picked = parseISO(value)
  if (Number.isNaN(picked.getTime())) return undefined

  // `format` reads the same local fields the picker and `zonedPickerDateToISO`
  // will, so this compares what would actually be SHOWN against what was asked
  // for — not the instant, which is meaningless on this path. Compared to the
  // minute, which is all this widget carries and all either producer emits.
  const asked = value.slice(0, DATETIME_LOCAL_MINUTE_LENGTH)
  return format(picked, DATETIME_LOCAL_PATTERN) === asked ? picked : undefined
}

/**
 * An instant → the `Date` a `Date`-valued picker must be given to SHOW
 * `timeZone`'s wall clock.
 *
 * @returns `undefined` when there is nothing selectable — including while the
 *          zone is still loading, so a form shows no date rather than the
 *          browser's reading of one
 */
export function toZonedPickerDate(
  instant: Instant,
  timeZone: string
): Date | undefined {
  return datetimeLocalToPickerDate(toZonedDatetimeLocalValue(instant, timeZone))
}

/**
 * The inverse: the wall clock a `Date`-valued picker handed back, read as
 * `timeZone`'s local time → the instant the API stores.
 *
 * @returns ISO 8601 with `Z`, or `null` when there is nothing to store — no
 *          value, or a zone that has not arrived yet. A caller treats `null` as
 *          « do not write », never as « write today »: the fields this guards
 *          are what an operator agreed with a client.
 */
export function zonedPickerDateToISO(
  picked: Date | null | undefined,
  timeZone: string
): string | null {
  if (!picked || Number.isNaN(picked.getTime())) return null

  return zonedDatetimeLocalToISO(
    format(picked, DATETIME_LOCAL_PATTERN),
    timeZone
  )
}

const DATETIME_LOCAL_SEPARATOR = 'T'

/**
 * A default for a `Date`-valued picker on which `day` was just picked: that day
 * at the hour `timeZone` reads at `earliest` — or `earliest`'s own day when
 * `day` is already behind it on the ZONE's calendar, so a default is never in
 * the past (late evening, or a site already on tomorrow).
 *
 * @returns `undefined` when the zone cannot be placed or the wall clock cannot
 *          be held by the browser — the caller keeps the bare day
 */
export function zonedPickerDefaultNoEarlierThan(
  day: Date,
  earliest: Instant,
  timeZone: string
): Date | undefined {
  const lead = toZonedDatetimeLocalValue(earliest, timeZone)
  if (!lead) return undefined

  const [leadDay, leadTime] = lead.split(DATETIME_LOCAL_SEPARATOR)
  const pickedDay = format(day, ISO_DAY_PATTERN)
  const onDay = pickedDay < leadDay ? leadDay : pickedDay

  return datetimeLocalToPickerDate(
    `${onDay}${DATETIME_LOCAL_SEPARATOR}${leadTime}`
  )
}

const ZONE_LABEL_CACHE = new Map<string, string>()

/** The api's `formatZoneLabelFr`, resolved at `now` for the same reason. */
function formatZoneLabelFr(timeZone: string): string {
  const cached = ZONE_LABEL_CACHE.get(timeZone)
  if (cached) return cached

  const label =
    new Intl.DateTimeFormat(DEFAULT_LOCALE, {
      timeZone,
      timeZoneName: 'longGeneric',
    })
      .formatToParts(new Date())
      .find((part) => part.type === 'timeZoneName')?.value ?? timeZone

  ZONE_LABEL_CACHE.set(timeZone, label)
  return label
}

/**
 * The French name of `timeZone`'s clock when it reads differently from Paris's
 * at `instant`, else `null` — datetime standard rule 7, the conditional label
 * of #1974. Offsets are compared rather than names, so Brussels earns no label
 * and Casablanca earns one only in Paris's summer. Same wording as the api's
 * `withZoneLabelFr`, so a client email and the agenda name one site alike.
 */
export function zoneLabelFrOrNull(
  timeZone: string,
  instant: Instant
): string | null {
  if (!instant || timeZone === DEFAULT_TIMEZONE || !isUsableZone(timeZone)) {
    return null
  }

  const at = toDate(instant)
  if (Number.isNaN(at.getTime())) return null

  try {
    if (zoneOffsetMs(timeZone, at) === zoneOffsetMs(DEFAULT_TIMEZONE, at)) {
      return null
    }

    return formatZoneLabelFr(timeZone)
  } catch {
    return null
  }
}

/** The zone of the person reading the screen — rule 7's « local hour ». */
export function viewerTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}
